/**
 * De lezer van `sub_bron_signalen` — het scherm waarop de voorgestelde
 * wijzigingen worden beoordeeld (T7.2).
 *
 * ── WAT ER AL WAS, EN WAT ER ONTBRAK ──────────────────────────────────────
 *
 * Migratie 063 zette twee tabellen neer en de edge-functie
 * `supabase/functions/subsidie-bewaking/index.ts` vult ze: `sub_bron_signalen`
 * draagt per signaal `huidige_waarde` (wat de database beweert), `bron_waarde`
 * (wat de bron zegt) en `bron_tekst` (het fragment waarin gezocht is). De diff
 * en de brontekst-ernaast uit het ticket BESTONDEN dus al als data. Wat
 * ontbrak was een lezer: op 08-09-2026 gaf `grep -rn sub_bron_signalen src/`
 * NUL treffers. De signalen landden in een tabel waar niemand keek.
 *
 * ── WAT "BEVESTIGEN" DOET, EN WAAROM HET NIETS DOORVOERT ──────────────────
 *
 * Bevestigen betekent hier "gezien en akkoord, ik pas het zelf aan" en NIET
 * "voer de wijziging door". Dat is geen halve oplossing maar de enige eerlijke:
 * de bewaking DRAAGT geen nieuwe waarde. Geteld in de elf plekken waar
 * `supabase/functions/subsidie-bewaking/index.ts` een signaal opbouwt, staat
 * `bron_waarde` negen keer expliciet op `null` (met de reden erbij op
 * index.ts:733-736 en index.ts:884-886), en de twee keer dat hij gevuld is, is
 * het geen kandidaatwaarde voor het veld dat het signaal noemt:
 *
 *   index.ts:775  `bron_waarde: String(nu.status)` bij `veld: 'rvo_url'`
 *                 → dat is een HTTP-statuscode; die in `rvo_url` schrijven zou
 *                   van "404" een adres maken.
 *   index.ts:847  `bron_waarde: nu.hash` bij `veld: null`
 *                 → een tekst-hash zonder doelkolom.
 *
 * Automatisch doorvoeren zou dus betekenen dat dit scherm een waarde verzint
 * die de bewaking bewust weigert te verzinnen — de twee metingen daarachter
 * (Flex-E publiceert budget per onderdeel op drie pagina's; twee RVO-pagina's
 * gaven een 404) staan in de kop van de edge-functie. `heeftVoorstel()`
 * hieronder maakt die keuze toetsbaar in plaats van vanzelfsprekend: zodra een
 * bewakingslaag wél een waarde voorstelt, wordt hij `true` en gaat
 * `tests/subsidie/signalen.node.test.mjs` rood met de mededeling dat er een
 * doorvoerpad bij moet.
 *
 * ── EN TOCH IS DE KLIK NIET LEEG ──────────────────────────────────────────
 *
 * `status` heeft een tweede lezer buiten dit scherm, en die is aanwijsbaar:
 * `schrijfSignaal` in de edge-functie (index.ts:1136-1163) pakt het laatste
 * signaal per sleutel ongeacht status, en geeft `'afgehandeld'` terug — geen
 * nieuw signaal, geen tweede notificatie — zolang `huidige_waarde` en
 * `bron_waarde` gelijk zijn gebleven. Bevestigen of afwijzen legt een
 * terugkerende toestand dus echt stil, en een verschoven waarde komt terug als
 * nieuw signaal. Dat is precies wat `vervolgActie()` aan de gebruiker vertelt.
 *
 * ── WAAROM DE SUPABASE-IMPORT ONDERIN STAAT ───────────────────────────────
 *
 * Alles boven `── REACT-SCHIL ──` is puur: geen client, geen `@/`-alias, geen
 * `new Date()` binnenin. Daardoor kan `tests/subsidie/signalen.node.test.mjs`
 * dit bestand rechtstreeks laden onder `node --test`, net als
 * `useSubsidieDocumenten.ts` en de edge-functie zelf.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/* ── PUUR ──────────────────────────────────────────────────────────────── */

/**
 * De elf waarden van `sub_bron_signalen.soort`, in de volgorde van de CHECK in
 * `docs/laadplein-designer/migrations/063_subsidie_bewaking.forward.sql:87-96`.
 * De test legt deze lijst naast dat bestand, zodat een twaalfde soort in de
 * migratie niet stilletjes als onbekend door dit scherm valt.
 */
export const SIGNAAL_SOORTEN = [
  'controle_verouderd',
  'venster_opent',
  'venster_sluit',
  'concept_venster',
  'venster_verlopen',
  'budget_onbekend',
  'budget_krap',
  'bron_onbereikbaar',
  'bron_gewijzigd',
  'waarde_niet_gevonden',
] as const;

export type SignaalSoort = (typeof SIGNAAL_SOORTEN)[number];

export const SIGNAAL_ERNSTEN = ['info', 'waarschuwing', 'urgent'] as const;
export type SignaalErnst = (typeof SIGNAAL_ERNSTEN)[number];

export const SIGNAAL_STATUSSEN = ['concept', 'bevestigd', 'afgewezen', 'verlopen'] as const;
export type SignaalStatus = (typeof SIGNAAL_STATUSSEN)[number];

/**
 * De twee statussen die een mens zet. `verlopen` zet de bewaking zelf zodra een
 * toestand voorbij is (063:126); `concept` is de enige die ze aanmaakt.
 */
export const MENSELIJKE_BESLUITEN = ['bevestigd', 'afgewezen'] as const;
export type Besluit = (typeof MENSELIJKE_BESLUITEN)[number];

/**
 * De rollen die `sub_bron_signalen` mogen SCHRIJVEN.
 *
 * Gemeten in `pg_policies` op 08-09-2026: `sub_bron_signalen_select_team` laat
 * drie rollen lezen (admin, super_admin, crm_user), `sub_bron_signalen_beheer`
 * (FOR ALL) laat er twee schrijven. Zonder deze constante zou het scherm voor
 * een crm_user twee knoppen tonen die niets doen — en dat is bij UPDATE geen
 * foutmelding maar stilte, want een rij buiten de policy telt niet mee.
 */
export const SIGNAAL_BEHEER_ROLLEN: readonly string[] = ['admin', 'super_admin'];

export interface BronSignaal {
  id: string;
  regeling_id: string;
  soort: SignaalSoort;
  ernst: SignaalErnst;
  titel: string;
  toelichting: string | null;
  /** De kolom in `sub_regelingen` waar het over gaat, of null. */
  veld: string | null;
  /** Wat de database beweert, als tekst. */
  huidige_waarde: string | null;
  /** Wat in de bron gevonden is. Vrijwel altijd null — zie `heeftVoorstel`. */
  bron_waarde: string | null;
  bron_url: string | null;
  /** Het fragment brontekst waarin gezocht is: het bewijs onder het signaal. */
  bron_tekst: string | null;
  status: SignaalStatus;
  sleutel: string;
  eerst_gezien_op: string | null;
  laatst_gezien_op: string | null;
  afgehandeld_op: string | null;
  afhandel_notitie: string | null;
  /**
   * De regeling waar het signaal aan hangt, uit de meegelezen `sub_regelingen`.
   * `titel` noemt de korte naam ook, maar die zit dan in een zin; voor groeperen
   * en voor de kop van de kaart wil je hem los.
   */
  regeling_code: string | null;
  regeling_naam: string | null;
}

function tekstOfNull(waarde: unknown): string | null {
  return typeof waarde === 'string' && waarde.length > 0 ? waarde : null;
}

/** De meegelezen regeling, of een leeg object als de embed er niet is. */
function leesRegeling(waarde: unknown): Record<string, unknown> {
  return waarde !== null && typeof waarde === 'object' && !Array.isArray(waarde)
    ? (waarde as Record<string, unknown>)
    : {};
}

function isSoort(waarde: unknown): waarde is SignaalSoort {
  return typeof waarde === 'string' && (SIGNAAL_SOORTEN as readonly string[]).includes(waarde);
}

/**
 * Eén databaserij naar een signaal.
 *
 * Een onbekende `soort` valt terug op `bron_gewijzigd` — de soort met de meest
 * terughoudende vervolgactie ("leg de regeling naast de bron"). Dat is bewust
 * de veilige kant: een twaalfde soort die dit scherm nog niet kent, moet naar
 * de bron verwijzen en niet naar een veld dat hij niet heeft.
 */
export function normaliseerSignaal(rij: Record<string, unknown>): BronSignaal {
  const ernst = rij.ernst;
  const status = rij.status;
  const regeling = leesRegeling(rij.sub_regelingen);
  return {
    id: String(rij.id ?? ''),
    regeling_id: String(rij.regeling_id ?? ''),
    soort: isSoort(rij.soort) ? rij.soort : 'bron_gewijzigd',
    ernst: (SIGNAAL_ERNSTEN as readonly unknown[]).includes(ernst)
      ? (ernst as SignaalErnst)
      : 'waarschuwing',
    titel: String(rij.titel ?? ''),
    toelichting: tekstOfNull(rij.toelichting),
    veld: tekstOfNull(rij.veld),
    huidige_waarde: tekstOfNull(rij.huidige_waarde),
    bron_waarde: tekstOfNull(rij.bron_waarde),
    // Valt terug op de `rvo_url` van de regeling: een signaal zonder eigen
    // bron_url (elk toestandsignaal van laag 1 en 2 bij een regeling zonder
    // adres) houdt dan tóch een klikbare bron.
    bron_url: tekstOfNull(rij.bron_url) ?? tekstOfNull(regeling.rvo_url),
    bron_tekst: tekstOfNull(rij.bron_tekst),
    status: (SIGNAAL_STATUSSEN as readonly unknown[]).includes(status)
      ? (status as SignaalStatus)
      : 'concept',
    sleutel: String(rij.sleutel ?? ''),
    eerst_gezien_op: tekstOfNull(rij.eerst_gezien_op),
    laatst_gezien_op: tekstOfNull(rij.laatst_gezien_op),
    afgehandeld_op: tekstOfNull(rij.afgehandeld_op),
    afhandel_notitie: tekstOfNull(rij.afhandel_notitie),
    regeling_code: tekstOfNull(regeling.code),
    regeling_naam: tekstOfNull(regeling.korte_naam) ?? tekstOfNull(regeling.code),
  };
}

/**
 * De soorten waarbij `bron_waarde` een DIAGNOSE is en geen kandidaatwaarde voor
 * `veld`. Zie de kop van dit bestand voor de twee regelnummers.
 */
export const DIAGNOSTISCHE_BRONWAARDE: readonly SignaalSoort[] = [
  'bron_onbereikbaar',
  'bron_gewijzigd',
];

/**
 * Draagt dit signaal een waarde die je zóu kunnen doorvoeren?
 *
 * Vandaag geeft dit voor elk signaal dat de bewaking kan maken `false`, en dat
 * is de reden dat er geen doorvoerknop op dit scherm staat. De functie bestaat
 * om die bewering toetsbaar te houden: `tests/subsidie/signalen.node.test.mjs`
 * laat de vier signaalfabrieken van de edge-functie los op de zes echte
 * regelingen en eist dat hier overal `false` uit komt. Wordt de bewaking ooit
 * uitgebreid met een laag die wél een waarde vaststelt, dan valt die test om en
 * is dat het sein om een doorvoerpad te bouwen — niet om deze functie te
 * versoepelen.
 */
export function heeftVoorstel(signaal: BronSignaal): boolean {
  if (signaal.veld === null || signaal.bron_waarde === null) return false;
  if (DIAGNOSTISCHE_BRONWAARDE.includes(signaal.soort)) return false;
  return true;
}

/** Waar de gebruiker naartoe moet om het signaal echt op te lossen. */
export type VervolgPlek = 'beheer' | 'agenda' | 'bron';

export interface Vervolg {
  /** De kolom die hij zelf moet bijwerken, of null als er niets te wijzigen valt. */
  veld: string | null;
  plek: VervolgPlek;
  /** Eén zin, imperatief, die op de kaart onder de knoppen staat. */
  wat: string;
}

/**
 * Wat de gebruiker ná het bevestigen zelf moet doen.
 *
 * Dit is de tegenhanger van de keuze om niets automatisch door te voeren. Een
 * scherm dat alleen "bevestigd" registreert en niet zegt wat er dan moet
 * gebeuren, is de knop die vandaag zes keer is gevonden: hij meldt succes en
 * verandert niets. De tekst noemt daarom de kolom bij naam en het tabblad waar
 * die kolom te wijzigen is.
 *
 * De `switch` is uitputtend over `SignaalSoort` en heeft geen `default`. Dat is
 * met opzet: een twaalfde soort geeft dan een typefout hier, in plaats van een
 * lege regel op het scherm.
 */
export function vervolgActie(signaal: BronSignaal): Vervolg {
  switch (signaal.soort) {
    case 'controle_verouderd':
      return {
        veld: 'bron_gecontroleerd_op',
        plek: 'beheer',
        wat: 'Leg de regeling naast de RVO-pagina en druk daarna in Beheer op '
          + '"Gecontroleerd" — dat zet bron_gecontroleerd_op op vandaag. Bevestigen '
          + 'alleen verzet die datum niet.',
      };
    case 'venster_opent':
      return {
        veld: null,
        plek: 'agenda',
        wat: 'Er valt niets te wijzigen: dit is een agenda-signaal. Zorg dat de '
          + 'dossiers die mee willen op de openingsdatum compleet zijn.',
      };
    case 'venster_sluit':
      return {
        veld: null,
        plek: 'agenda',
        wat: 'Er valt niets te wijzigen: dit is een agenda-signaal. Plan het '
          + 'indienen vóór de sluitingsdatum.',
      };
    case 'concept_venster':
      return {
        veld: null,
        plek: 'agenda',
        wat: 'Er valt niets te wijzigen: zet de conceptaanvraag klaar. Klopt de '
          + 'datum niet, wijzig dan concept_vanaf in Beheer.',
      };
    case 'venster_verlopen':
      return {
        veld: 'status',
        plek: 'beheer',
        wat: 'Zet in Beheer de status recht, of vul het venster van de volgende '
          + 'ronde in. Tot dan toont het dashboard deze regeling als aanvraagbaar.',
      };
    case 'budget_onbekend':
      return {
        veld: 'budget_restant',
        plek: 'beheer',
        wat: 'Zoek het resterende budget op de RVO-pagina en vul budget_restant in '
          + 'Beheer in. Zolang dat veld leeg is, baseert de intake haar advies op een '
          + 'pot waarvan niemand weet of er nog iets in zit.',
      };
    case 'budget_krap':
      return {
        veld: 'budget_restant',
        plek: 'beheer',
        wat: 'Weeg of lopende dossiers nog kans maken en werk budget_restant in '
          + 'Beheer bij zodra RVO een nieuw bedrag publiceert.',
      };
    case 'bron_onbereikbaar':
      return {
        veld: 'rvo_url',
        plek: 'beheer',
        wat: 'Zoek de nieuwe plek van de pagina en werk rvo_url in Beheer bij. '
          + 'Blijft dit signaal na de volgende run staan, dan is het niet tijdelijk.',
      };
    case 'bron_gewijzigd':
      return {
        veld: 'bron_gecontroleerd_op',
        plek: 'bron',
        wat: 'Open de bronpagina, leg de regeling ernaast en werk wat verschoven is '
          + 'bij in Beheer. Zet daarna bron_gecontroleerd_op op vandaag.',
      };
    case 'waarde_niet_gevonden':
      return {
        veld: signaal.veld,
        plek: 'bron',
        wat: 'Controleer deze waarde op de bronpagina. Staat hij er nog (anders '
          + 'geschreven, of op een subpagina), dan is afwijzen het juiste antwoord; '
          + 'is hij echt verschoven, wijzig hem dan in Beheer.',
      };
  }
}

const ERNST_VOLGORDE: Record<SignaalErnst, number> = { urgent: 0, waarschuwing: 1, info: 2 };

/** Urgent bovenaan, daarbinnen het langst openstaande signaal eerst. */
export function sorteerSignalen(signalen: readonly BronSignaal[]): BronSignaal[] {
  return [...signalen].sort((a, b) =>
    ERNST_VOLGORDE[a.ernst] - ERNST_VOLGORDE[b.ernst]
    || (a.eerst_gezien_op ?? '').localeCompare(b.eerst_gezien_op ?? '')
    || a.titel.localeCompare(b.titel, 'nl'));
}

/**
 * De toestand van de bewaking zelf — en dit is de reden dat dit scherm bestaat
 * zoals het bestaat.
 *
 * "Geen signalen" mag nooit als "alles in orde" gelezen worden zolang er niet
 * gemeten is. Dat verschil is precies de fout die dit project op 08-09-2026 vier
 * keer heeft gemaakt.
 *
 * Stand op 08-09-2026, ná de eerste echte run om 21:02: `sub_bron_signalen` 7
 * rijen (alle zeven concept), `sub_bron_snapshots` 0 rijen, en de pg_cron-job
 * `subsidie-bewaking-ouderdom` nul runs. Die run ging in de lichte modus
 * (`{"metBron": false}`), en die slaat laag 3 over — vandaar wel signalen en
 * geen momentopnames.
 *
 * Het onderscheid is uit leesbare data af te leiden, want de bewaking laat twee
 * verschillende sporen na:
 *   - laag 3 schrijft bij ELKE ophaalpoging een rij in `sub_bron_snapshots`,
 *     ook bij een 404 (index.ts:1300-1312). Nul momentopnames betekent dus dat
 *     de edge-functie nooit een bron heeft aangeraakt.
 *   - de SQL-dodemansknop (migratie 063) schrijft alleen signalen en géén
 *     momentopname. Signalen zonder momentopnames = alleen laag 1 heeft
 *     gedraaid.
 */
export type BewakingsToestand =
  /** Nul signalen én nul momentopnames: er heeft nooit iets gedraaid. */
  | 'nooit_gedraaid'
  /**
   * Wel signalen, geen momentopnames: er is nooit een bronpagina opgehaald.
   *
   * Bewust NIET 'alleen_ouderdomswacht' genoemd, zoals hij eerst heette. Twee
   * schrijvers leveren precies dit beeld op — de SQL-dodemansknop uit migratie
   * 063, en de edge-functie in de lichte modus (`{"metBron": false}`), die laag
   * 3 overslaat. Op 08-09-2026 was het de tweede: zeven signalen om 21:02,
   * terwijl de cron nul runs had. Deze twee tellingen kunnen dat verschil niet
   * zien, dus hoort de naam het ook niet te beweren.
   */
  | 'zonder_bronophaling'
  /** Er is een bron opgehaald en er staat niets open. */
  | 'niets_open'
  /** Er staan concepten klaar. */
  | 'open'
  /** De momentopnames waren niet te tellen; "leeg" is dan niet te duiden. */
  | 'onbekend';

/**
 * `aantalMomentopnames` is bewust `number | null`. Mislukt de telling, dan is
 * nul een AANNAME en geen meting, en zou deze functie van een kapotte query
 * "de bewaking heeft nooit gedraaid" maken — een bewering zonder grond, precies
 * de fout die dit scherm moet vermijden. `null` betekent daarom "niet gemeten"
 * en levert `'onbekend'`.
 */
export function bepaalBewakingsToestand(meting: {
  aantalSignalen: number;
  aantalConcepten: number;
  aantalMomentopnames: number | null;
}): BewakingsToestand {
  if (meting.aantalConcepten > 0) return 'open';
  if (meting.aantalMomentopnames === null) return 'onbekend';
  if (meting.aantalMomentopnames > 0) return 'niets_open';
  if (meting.aantalSignalen > 0) return 'zonder_bronophaling';
  return 'nooit_gedraaid';
}

export interface ToestandTekst {
  kop: string;
  uitleg: string;
  /** Waar of dit een probleem is dat aandacht vraagt (rand + kleur op het scherm). */
  vraagtAandacht: boolean;
}

/**
 * De woorden bij de toestand. Bewust hier en niet in de component, zodat de
 * test kan afdwingen dat de lege staat nooit "alles in orde" zegt zolang er
 * niet gemeten is.
 */
export function toestandTekst(toestand: BewakingsToestand): ToestandTekst {
  switch (toestand) {
    case 'nooit_gedraaid':
      return {
        kop: 'De bewaking heeft nog niet gedraaid.',
        uitleg:
          'Er staan nul signalen en nul momentopnames. Dat is iets anders dan '
          + '"geen nieuws": er is nooit een RVO-pagina opgehaald en nooit een '
          + 'controle gedaan. Draai hem hieronder een keer, of koppel er een '
          + 'planner aan.',
        vraagtAandacht: true,
      };
    case 'zonder_bronophaling':
      return {
        kop: 'Er is nog geen bronpagina opgehaald.',
        uitleg:
          'Er zijn signalen, maar nul momentopnames — laag 3, de vergelijking '
          + 'met de RVO-pagina zelf, heeft dus nog nooit gedraaid. Wat hier staat '
          + 'komt uit rekenwerk op de eigen database: vensters, budgetten en de '
          + 'ouderdom van de laatste controle. Dat kan kloppen en toch achterlopen '
          + 'op wat RVO vandaag publiceert. Draai de bewaking hieronder een keer '
          + 'mét bron.',
        vraagtAandacht: true,
      };
    case 'niets_open':
      return {
        kop: 'Niets open.',
        uitleg:
          'De bewaking heeft gedraaid en er staat geen enkel signaal open. Dit is '
          + 'de enige toestand waarin een leeg scherm ook echt "geen nieuws" '
          + 'betekent.',
        vraagtAandacht: false,
      };
    case 'open':
      return {
        kop: 'Er staan signalen open.',
        uitleg: 'Elk signaal hieronder is een concept: niets is doorgevoerd.',
        vraagtAandacht: true,
      };
    case 'onbekend':
      return {
        kop: 'Onbekend of de bewaking heeft gedraaid.',
        uitleg:
          'De momentopnames konden niet worden geteld. Of dit scherm leeg is omdat '
          + 'er niets aan de hand is, of omdat er nooit gekeken is, valt daarmee niet '
          + 'te zeggen.',
        vraagtAandacht: true,
      };
  }
}

/** De uitkomst van één run van de edge-functie, zoals hij hem teruggeeft. */
export interface Runuitkomst {
  ok?: boolean;
  modus?: string;
  metBron?: boolean;
  regelingen?: number;
  nieuw?: number;
  mislukt?: number;
  duurMs?: number;
  resultaten?: Array<{ code?: string; nieuw?: number; vervallen?: number; laag3?: string; fout?: string }>;
}

/**
 * De run in één zin, en die zin mag nooit vrolijker zijn dan de uitkomst.
 *
 * `behandelVerzoek` geeft `ok: false` met HTTP 500 zodra ook maar één regeling
 * mislukt (index.ts:1543-1556). Een samenvatting die alleen `nieuw` zou tellen,
 * zou een run waarin vijf van de zes bronnen onbereikbaar waren als succes
 * afdrukken.
 */
export function vatRunSamen(uitkomst: Runuitkomst | null | undefined): string {
  if (!uitkomst) return 'De bewaking gaf geen leesbaar antwoord terug.';
  const regelingen = uitkomst.regelingen ?? 0;
  const nieuw = uitkomst.nieuw ?? 0;
  const mislukt = uitkomst.mislukt ?? (uitkomst.resultaten ?? []).filter(r => r.fout).length;
  const kern = `${regelingen} ${regelingen === 1 ? 'regeling' : 'regelingen'} langsgelopen, `
    + `${nieuw} ${nieuw === 1 ? 'nieuw signaal' : 'nieuwe signalen'}`;
  if (mislukt > 0) {
    const namen = (uitkomst.resultaten ?? [])
      .filter(r => r.fout)
      .map(r => r.code ?? '?')
      .join(', ');
    return `${kern}. ${mislukt} mislukt (${namen}) — de bewaking is niet compleet gedraaid.`;
  }
  return `${kern}.`;
}

/**
 * De cachesleutels.
 *
 * Beginnen met `'sub_bron_signalen'` respectievelijk `'sub_bron_snapshots'`,
 * zodat een invalidatie op de prefix beide meeneemt zonder de sleutels van
 * `useSubsidieRegelingen` te raken.
 */
export function signalenQuerySleutel(jaar: number): (string | number)[] {
  return ['sub_bron_signalen', jaar];
}

export function momentopnamesQuerySleutel(jaar: number): (string | number)[] {
  return ['sub_bron_snapshots', 'telling', jaar];
}

/* ── REACT-SCHIL ───────────────────────────────────────────────────────── */

/**
 * `src/integrations/supabase/types.ts` staat op slot (v1-guard) en is niet
 * opnieuw gegenereerd na migratie 063: `grep -c sub_bron_signalen` op dat
 * bestand geeft 0 op 08-09-2026. PostgREST levert de tabellen gewoon, maar
 * TypeScript kent ze niet en weigert elke `.from()` erop. Vandaar deze twee
 * ontsnappingen, exact hetzelfde patroon als `regelingenTabel()` in
 * `useSubsidieBeheer.ts:39`. Ze kunnen weg zodra types.ts opnieuw is
 * gegenereerd.
 */
/**
 * De client wordt pas geladen wanneer er echt gequeryd wordt. Een `import`
 * bovenin zou het pure deel van dit bestand onlaadbaar maken voor `node --test`.
 * De module-cache van de bundler maakt de tweede aanroep gratis.
 */
async function laadClient() {
  const mod = await import('@/integrations/supabase/client');
  return mod.supabase;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function signalenTabel(): Promise<any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((await laadClient()) as any).from('sub_bron_signalen');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function momentopnamesTabel(): Promise<any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((await laadClient()) as any).from('sub_bron_snapshots');
}

/**
 * Postgres geeft bij een UPDATE die door RLS wordt geblokkeerd GEEN fout: de rij
 * valt buiten de policy, het statement raakt nul rijen en PostgREST meldt
 * succes. Dat is de val die in dit project vandaag drie keer is gesprongen.
 * Elke schrijfactie hieronder vraagt daarom `select('id')` terug en telt hier
 * wat er werkelijk is geraakt — hetzelfde patroon als `eisGeraakteRij` in
 * `useSubsidieBeheer.ts:128`.
 */
function eisGeraakteRij(rijen: unknown) {
  if (Array.isArray(rijen) && rijen.length > 0) return;
  throw new Error(
    'Het signaal is niet gewijzigd. Twee mogelijkheden: het stond niet meer op '
    + 'concept (iemand anders was je voor — ververs het scherm), of je account mag '
    + 'niet schrijven. Afhandelen mag alleen admin en super_admin (policy '
    + 'sub_bron_signalen_beheer, migratie 063).',
  );
}

export interface AfhandelInvoer {
  id: string;
  besluit: Besluit;
  notitie?: string | null;
}

/**
 * De signalen van één jaargang, plus de tellingen die de lege staat eerlijk
 * maken.
 */
export function useSubsidieSignalen(jaar = 2026) {
  const queryClient = useQueryClient();

  const signalenQuery = useQuery({
    queryKey: signalenQuerySleutel(jaar),
    queryFn: async () => {
      // `!inner` op de regeling: signalen horen bij de jaargang die dit scherm
      // toont. Zonder die filter zou een signaal van 2025 tussen de 2026-kaarten
      // staan zonder dat de kop het zegt.
      const { data, error } = await (await signalenTabel())
        .select('*, sub_regelingen!inner(id, code, korte_naam, jaar, rvo_url)')
        .eq('sub_regelingen.jaar', jaar)
        .order('laatst_gezien_op', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Record<string, unknown>[];
    },
  });

  /**
   * Alleen TELLEN, en bewust zonder `tekst`: die kolom draagt de volledige
   * genormaliseerde pagina (tot tienduizenden tekens per rij). Voor het
   * onderscheid "heeft de bewaking ooit een bron aangeraakt" is het aantal
   * genoeg, en de datum van de laatste poging.
   */
  const momentopnamesQuery = useQuery({
    queryKey: momentopnamesQuerySleutel(jaar),
    queryFn: async () => {
      const { data, error, count } = await (await momentopnamesTabel())
        .select('id, opgehaald_op, http_status, sub_regelingen!inner(jaar)', { count: 'exact' })
        .eq('sub_regelingen.jaar', jaar)
        .order('opgehaald_op', { ascending: false })
        .limit(1);
      if (error) throw error;
      const laatste = (data ?? [])[0] as { opgehaald_op?: string } | undefined;
      return { aantal: count ?? 0, laatsteOphaling: laatste?.opgehaald_op ?? null };
    },
  });

  const ververs = () => {
    queryClient.invalidateQueries({ queryKey: ['sub_bron_signalen'] });
    queryClient.invalidateQueries({ queryKey: ['sub_bron_snapshots'] });
  };

  const handelAf = useMutation({
    mutationFn: async ({ id, besluit, notitie }: AfhandelInvoer) => {
      const supabase = await laadClient();
      const { data: gebruiker } = await supabase.auth.getUser();
      const nu = new Date().toISOString();
      const { data, error } = await (await signalenTabel())
        .update({
          status: besluit,
          afgehandeld_door: gebruiker?.user?.id ?? null,
          afgehandeld_op: nu,
          afhandel_notitie: notitie && notitie.length > 0 ? notitie : null,
          updated_at: nu,
        })
        .eq('id', id)
        // Alleen een openstaand concept afhandelen. Zonder deze filter zou een
        // tweede tabblad een al genomen besluit stil overschrijven.
        .eq('status', 'concept')
        .select('id');
      if (error) throw error;
      eisGeraakteRij(data);
      return besluit;
    },
    onSuccess: async (besluit) => {
      ververs();
      const { toast } = await import('sonner');
      toast.success(
        besluit === 'bevestigd'
          ? 'Signaal bevestigd. De wijziging zelf staat nog open — zie de vervolgstap op de kaart.'
          : 'Signaal afgewezen.',
      );
    },
    onError: async (error: unknown) => {
      const { toast } = await import('sonner');
      toast.error(`Afhandelen mislukt: ${error instanceof Error ? error.message : String(error)}`);
    },
  });

  /**
   * De bewaking nu laten draaien.
   *
   * Zonder deze knop zou de lege staat een doodlopende weg zijn: de functie is
   * uitgerold, maar er is geen planner aan gekoppeld (zie de vier openstaande
   * punten onderaan `063_subsidie_bewaking.forward.sql`). De functie doet zelf
   * de rolcontrole (index.ts:1417-1424, admin of super_admin) en schrijft
   * uitsluitend in `sub_bron_signalen`, `sub_bron_snapshots` en `notifications`.
   */
  const draaiBewaking = useMutation({
    mutationFn: async (): Promise<Runuitkomst> => {
      const supabase = await laadClient();
      const { data, error } = await supabase.functions.invoke('subsidie-bewaking', {
        body: { metBron: true },
      });
      if (error) {
        // Bij een 4xx/5xx geeft supabase-js de body niet in `data` maar in
        // `error.context`. Zonder deze uitpakking zou een 401 ("alleen admin")
        // als het generieke "Edge Function returned a non-2xx status code"
        // op het scherm komen, en dan zoekt iemand in de verkeerde hoek.
        let detail = error instanceof Error ? error.message : String(error);
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const body = await (error as any)?.context?.json?.();
          if (body?.fout) detail = String(body.fout);
        } catch {
          /* body niet leesbaar — dan blijft de oorspronkelijke melding staan */
        }
        throw new Error(detail);
      }
      const uitkomst = (data ?? {}) as Runuitkomst;
      // Een run die niet compleet is, mag geen succes heten. De functie geeft
      // daarvoor `ok: false`; die vlag is hier het enige wat telt.
      if (uitkomst.ok !== true) throw new Error(vatRunSamen(uitkomst));
      return uitkomst;
    },
    onSuccess: async (uitkomst) => {
      ververs();
      const { toast } = await import('sonner');
      toast.success(`Bewaking gedraaid: ${vatRunSamen(uitkomst)}`);
    },
    onError: async (error: unknown) => {
      const { toast } = await import('sonner');
      toast.error(`Bewaking draaien mislukt: ${error instanceof Error ? error.message : String(error)}`);
    },
  });

  const alle = (signalenQuery.data ?? []).map(rij => normaliseerSignaal(rij));
  const concepten = sorteerSignalen(alle.filter(s => s.status === 'concept'));
  const afgehandeld = alle.filter(s => s.status !== 'concept');
  /**
   * `null` zodra de telling mislukte: dan is nul een aanname en geen meting.
   * `bepaalBewakingsToestand` maakt daar `'onbekend'` van in plaats van
   * "nooit gedraaid".
   */
  const aantalMomentopnames = momentopnamesQuery.error ? null : (momentopnamesQuery.data?.aantal ?? 0);

  return {
    concepten,
    afgehandeld,
    toestand: bepaalBewakingsToestand({
      aantalSignalen: alle.length,
      aantalConcepten: concepten.length,
      aantalMomentopnames,
    }),
    aantalMomentopnames,
    laatsteOphaling: momentopnamesQuery.data?.laatsteOphaling ?? null,
    handelAf,
    draaiBewaking,
    isLoading: signalenQuery.isLoading || momentopnamesQuery.isLoading,
    /** Alleen de lezing van de signalen zelf is fataal voor dit scherm. */
    error: signalenQuery.error,
    momentopnamesFout: momentopnamesQuery.error,
  };
}
